import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Alert, InputAdornment,
  Chip, CircularProgress, Paper, IconButton,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, FormGroup,
  Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemButton, ListItemText, Divider,
} from '@mui/material';
import {
  Save, ArrowBack, PhotoCamera, Delete, CheckCircle, Search,
  History, AddCircleOutline,
} from '@mui/icons-material';
import CustomerLayout from '../components/CustomerLayout';
import { customerOrdersAPI, customerAuthAPI } from '../services/api';

const DISPATCH_TYPES = [
  { key: 'urgent',     label: '긴급' },
  { key: 'round_trip', label: '왕복' },
  { key: 'waypoint',   label: '경유' },
];

const HANDLING_METHODS = ['지게차', '호이스트', '인력 제공', '수작업', '크레인'];

const VEHICLE_TYPES = [
  '차종 무관', '카고', '카고/탑', '카고리프트', '윙바디', '윙바디/탑', '윙+', '탑',
  '탑리프트', '리프트', '리프트윙', '호로리프트', '호로', '추레라', '다마스', '라보',
  '무진동', '초장축', '냉장탑', '냉동탑', '냉장윙', '냉동탑리프트', '냉장윙리프트',
];

const WEIGHT_OPTIONS = [
  '0.3t', '0.5t', '1t', '1t초장축', '1.4t', '1.4t초장축', '2.5t', '3.5t', '3.5t광폭',
  '4t', '5t', '5플러스', '5t축', '5t플축', '8t', '9.5t', '11t', '14t', '16t', '18t',
  '22t', '25t',
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1~12
const MINUTES = [0, 10, 20, 30, 40, 50];

// 프리셋 → 기준 날짜 (자정)
function calcPresetDate(preset) {
  const now = new Date();
  if (preset === 'now')      return now;
  if (preset === 'today')    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'tomorrow') { const d = new Date(now); d.setDate(d.getDate() + 1); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  if (preset === 'monday')   {
    const d = new Date(now);
    const day = d.getDay();
    const daysUntilMon = (day === 1) ? 7 : ((8 - day) % 7) || 7;
    d.setDate(d.getDate() + daysUntilMon);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
}

// 폼 시간(시:분 + 오전/오후)을 기준 날짜와 결합해 Date 반환
function combineDateTime(baseDate, ampm, hour, minute) {
  if (!baseDate) return null;
  let h = hour;
  if (ampm === 'AM') h = (hour === 12) ? 0 : hour;
  else h = (hour === 12) ? 12 : hour + 12;
  return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h, minute);
}

// MySQL 호환 datetime 문자열 (YYYY-MM-DD HH:MM:SS)
function toMysqlDt(d) {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function formatDateLabel(d) {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}(${days[d.getDay()]})`;
}

const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 8 && !digits.startsWith('0')) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};

// 다이얼로그: 이전 주문에서 출발지/도착지 정보 불러오기
function PreviousOrdersDialog({ open, onClose, onPick }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    customerOrdersAPI.getAll()
      .then((res) => setOrders((res.data?.data || []).slice(0, 20)))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>이전 주문에서 불러오기</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : orders.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>이전 주문이 없습니다.</Box>
        ) : (
          <List disablePadding>
            {orders.map((o) => (
              <React.Fragment key={o.order_id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { onPick(o); onClose(); }}>
                    <ListItemText
                      primary={`${o.order_number} · ${o.created_at ? new Date(o.created_at).toLocaleDateString('ko-KR') : ''}`}
                      secondary={`${o.origin_address || '-'} → ${o.destination_address || '-'}`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondaryTypographyProps={{ noWrap: false }}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}

// 일시 카드 컴포넌트
function DateTimePresetField({ title, presets, value, onChange, bgcolor }) {
  // value: { preset, ampm, hour, minute, customDate? }
  const ymdOf = (d) => d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';
  const autoDate = calcPresetDate(value.preset);
  const baseDate = value.customDate ? new Date(value.customDate + 'T00:00:00') : autoDate;

  const handlePresetChange = (next) => {
    // 프리셋 변경 시 customDate 를 그 프리셋의 자동 계산 날짜로 reset
    const auto = next === 'now' ? new Date() : calcPresetDate(next);
    if (!auto) return onChange({ ...value, preset: next, customDate: '' });
    const ymd = ymdOf(auto);
    if (next === 'now') {
      // "지금" 선택 시 현재 시각도 시간 슬롯에 반영
      const h24 = auto.getHours();
      const ampm = h24 < 12 ? 'AM' : 'PM';
      let hour = h24 % 12; if (hour === 0) hour = 12;
      const minute = (Math.round(auto.getMinutes() / 10) * 10) % 60;
      onChange({ ...value, preset: next, customDate: ymd, ampm, hour, minute });
    } else {
      onChange({ ...value, preset: next, customDate: ymd });
    }
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 2, ...(bgcolor ? { bgcolor } : {}) }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>{title}</Typography>
        <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small" color="primary" exclusive
            value={value.preset}
            onChange={(_, v) => v && handlePresetChange(v)}
            sx={{ flexWrap: 'wrap' }}
          >
            {presets.map((p) => (
              <ToggleButton key={p.key} value={p.key} sx={{ px: 2 }}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            type="date" size="small"
            value={value.customDate || ymdOf(autoDate)}
            onChange={(e) => onChange({ ...value, customDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
        </Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <ToggleButtonGroup
              size="small" exclusive value={value.ampm}
              onChange={(_, v) => v && onChange({ ...value, ampm: v })}
            >
              <ToggleButton value="AM">오전</ToggleButton>
              <ToggleButton value="PM">오후</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>시</InputLabel>
              <Select label="시" value={value.hour} onChange={(e) => onChange({ ...value, hour: e.target.value })}>
                {HOURS.map((h) => <MenuItem key={h} value={h}>{h}시</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>분</InputLabel>
              <Select label="분" value={value.minute} onChange={(e) => onChange({ ...value, minute: e.target.value })}>
                {MINUTES.map((m) => <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}분</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function CreateOrderPage({ onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [prevDialogOpen, setPrevDialogOpen] = useState(false);

  // 기본 폼
  const [form, setForm] = useState({
    dispatch_types: [],   // ['urgent','round_trip','waypoint']
    waypoints: [],        // [{ address: '' }]
    origin_address: '', origin_contact_name: '', origin_contact: '',
    destination_address: '', destination_contact_name: '', destination_contact: '',
    pickup:   { preset: 'today', ampm: 'AM', hour: 9, minute: 0 },
    delivery: { preset: 'today', ampm: 'PM', hour: 6, minute: 0 },
    loading_methods: [], unloading_methods: [],
    vehicle_types: [],    // 최대 3개
    vehicle_weights: [],  // 최대 3개
    special_notes: '',
  });

  const isWaypoint = form.dispatch_types.includes('waypoint');

  // 본인 거래처 정보 (체크박스 '내 정보에서 가져오기' 용)
  const [myInfo, setMyInfo] = useState(null);
  useEffect(() => {
    customerAuthAPI.me().then((res) => {
      const d = res.data?.data;
      if (d) setMyInfo(d);
    }).catch(() => {});
  }, []);

  // 체크박스 상태 (상차/하차/경유지[i])
  const [sameAsOrigin, setSameAsOrigin] = useState(false);
  const [sameAsDest, setSameAsDest] = useState(false);
  const [sameAsWp, setSameAsWp] = useState({}); // { 0: true, 1: false, ... }

  const customerAddr = () => (myInfo?.address || '');
  const customerName = () => (myInfo?.name || '');
  const customerPhone = () => {
    const p = myInfo?.phone || '';
    const isPlaceholder = p.startsWith('BN-') || p.startsWith('MIG-') || p.startsWith('TMP-');
    return isPlaceholder ? (myInfo?.office_phone || '') : (p || myInfo?.office_phone || '');
  };

  const handleSameAsOriginToggle = (checked) => {
    setSameAsOrigin(checked);
    setForm((p) => ({
      ...p,
      origin_address:      checked ? customerAddr()  : '',
      origin_contact_name: checked ? customerName()  : '',
      origin_contact:      checked ? formatPhone(customerPhone()) : '',
    }));
  };
  const handleSameAsDestToggle = (checked) => {
    setSameAsDest(checked);
    setForm((p) => ({
      ...p,
      destination_address:      checked ? customerAddr()  : '',
      destination_contact_name: checked ? customerName()  : '',
      destination_contact:      checked ? formatPhone(customerPhone()) : '',
    }));
  };
  const handleSameAsWpToggle = (idx, checked) => {
    setSameAsWp((s) => ({ ...s, [idx]: checked }));
    setForm((p) => {
      const next = [...p.waypoints];
      next[idx] = {
        ...next[idx],
        address:      checked ? customerAddr() : '',
        contact_name: checked ? customerName() : '',
        contact:      checked ? formatPhone(customerPhone()) : '',
      };
      return { ...p, waypoints: next };
    });
  };

  const toggleListItem = (field, value) => {
    const arr = form[field];
    const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
    setForm({ ...form, [field]: next });
  };

  const handleDispatchToggle = (key) => {
    const next = form.dispatch_types.includes(key)
      ? form.dispatch_types.filter((x) => x !== key)
      : [...form.dispatch_types, key];
    const update = { ...form, dispatch_types: next };
    // 경유 해제 시 경유지 비움
    if (key === 'waypoint' && !next.includes('waypoint')) update.waypoints = [];
    // 경유 추가 시 경유지가 비어 있으면 1개 추가
    if (key === 'waypoint' && next.includes('waypoint') && form.waypoints.length === 0) {
      update.waypoints = [{ address: '' }];
    }
    setForm(update);
  };

  const openAddressSearch = (cb) => {
    if (!window.daum || !window.daum.Postcode) return alert('주소 검색 서비스를 불러오는 중입니다.');
    new window.daum.Postcode({
      oncomplete: (data) => cb(data.roadAddress || data.jibunAddress),
    }).open();
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - photos.length;
    const newFiles = files.slice(0, remaining);
    setPhotos((prev) => [...prev, ...newFiles]);
    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreviews((prev) => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
  };
  const removePhoto = (i) => {
    setPhotos((prev) => prev.filter((_, idx) => idx !== i));
    setPhotoPreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handlePickPrevious = (o) => {
    // DB 의 JSON 컬럼은 드라이버 설정에 따라 string 또는 array 로 옴 — 안전 파서
    const parseJsonArr = (v) => {
      if (v == null || v === '') return [];
      if (Array.isArray(v)) return v;
      try { const r = JSON.parse(v); return Array.isArray(r) ? r : []; } catch { return []; }
    };
    // eslint-disable-next-line no-console
    console.log('[이전주문] 원본 응답:', o);
    const waypoints        = parseJsonArr(o.waypoints);
    const vehicleTypes     = parseJsonArr(o.vehicle_types);
    const vehicleWeights   = parseJsonArr(o.vehicle_weights);
    const loadingMethods   = parseJsonArr(o.loading_methods);
    const unloadingMethods = parseJsonArr(o.unloading_methods);
    const dispatchTypes    = parseJsonArr(o.dispatch_types);
    // eslint-disable-next-line no-console
    console.log('[이전주문] 파싱결과:', { waypoints, vehicleTypes, vehicleWeights, loadingMethods, unloadingMethods, dispatchTypes });

    // 경유지 유무에 맞춰 dispatch_types 의 'waypoint' 키를 동기화
    const baseDispatch = dispatchTypes.filter((k) => k !== 'waypoint');
    const finalDispatch = waypoints.length > 0
      ? Array.from(new Set([...baseDispatch, 'waypoint']))
      : baseDispatch;

    // 상/하차 일시(pickup/delivery)는 유지, 나머지는 이전 주문 값으로 명시적으로 덮어쓰기
    setForm((p) => ({
      ...p,
      origin_address:           o.origin_address || '',
      origin_contact:           formatPhone(o.origin_contact || ''),
      origin_contact_name:      o.origin_contact_name || '',
      destination_address:      o.destination_address || '',
      destination_contact:      formatPhone(o.destination_contact || ''),
      destination_contact_name: o.destination_contact_name || '',
      vehicle_types:            vehicleTypes,
      vehicle_weights:          vehicleWeights,
      loading_methods:          loadingMethods,
      unloading_methods:        unloadingMethods,
      dispatch_types:           finalDispatch,
      waypoints:                waypoints,
      special_notes:            o.special_notes || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.origin_address) return setError('출발지 주소를 입력하세요.');
    if (!form.destination_address) return setError('도착지 주소를 입력하세요.');
    if (isWaypoint && form.waypoints.some((w) => !w.address)) return setError('경유지 주소를 모두 입력하세요.');

    const resolveBase = (v) => v.customDate
      ? new Date(v.customDate + 'T00:00:00')
      : calcPresetDate(v.preset);
    const pickupBase   = resolveBase(form.pickup);
    const deliveryBase = resolveBase(form.delivery);
    const pickupDt   = combineDateTime(pickupBase, form.pickup.ampm, form.pickup.hour, form.pickup.minute);
    const deliveryDt = combineDateTime(deliveryBase, form.delivery.ampm, form.delivery.hour, form.delivery.minute);
    if (pickupDt && deliveryDt && deliveryDt < pickupDt) return setError('하차 일시가 상차 일시보다 빠릅니다.');

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('origin_address', form.origin_address);
      fd.append('origin_contact', form.origin_contact || '');
      fd.append('origin_contact_name', form.origin_contact_name || '');
      fd.append('destination_address', form.destination_address);
      fd.append('destination_contact', form.destination_contact || '');
      fd.append('destination_contact_name', form.destination_contact_name || '');
      if (pickupDt)   fd.append('pickup_datetime', toMysqlDt(pickupDt));
      if (deliveryDt) fd.append('desired_delivery_datetime', toMysqlDt(deliveryDt));
      if (form.special_notes) fd.append('special_notes', form.special_notes);
      // 호환용 단일 컬럼: 차종/톤수 첫 번째 값
      if (form.vehicle_types[0])   fd.append('required_vehicle_type', form.vehicle_types[0]);
      if (form.vehicle_weights[0]) fd.append('required_weight', form.vehicle_weights[0].replace(/[^\d.]/g, ''));
      // JSON 필드
      fd.append('dispatch_types',     JSON.stringify(form.dispatch_types));
      fd.append('waypoints',          JSON.stringify(form.waypoints));
      fd.append('loading_methods',    JSON.stringify(form.loading_methods));
      fd.append('unloading_methods',  JSON.stringify(form.unloading_methods));
      fd.append('vehicle_types',      JSON.stringify(form.vehicle_types));
      fd.append('vehicle_weights',    JSON.stringify(form.vehicle_weights));
      photos.forEach((file) => fd.append('photos', file));

      const res = await customerOrdersAPI.create(fd);
      setCreatedOrder(res.data.data);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || '주문 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted && createdOrder) {
    return (
      <CustomerLayout onLogout={onLogout}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 72, color: '#2e7d32', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>주문 접수 완료!</Typography>
          <Typography color="text.secondary" gutterBottom>주문번호: <strong>{createdOrder.order_number}</strong></Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => window.location.reload()}>새 주문 작성</Button>
            <Button variant="contained" onClick={() => navigate('/orders')}>내 주문 목록</Button>
          </Box>
        </Box>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout onLogout={onLogout}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>배차 주문 신청</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<History />} onClick={() => setPrevDialogOpen(true)}>
            이전 주문에서 불러오기
          </Button>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/orders')}>목록으로</Button>
        </Box>
      </Box>
      <PreviousOrdersDialog
        open={prevDialogOpen}
        onClose={() => setPrevDialogOpen(false)}
        onPick={handlePickPrevious}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* 구분 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>구분</Typography>
            <FormGroup row>
              {DISPATCH_TYPES.map((d) => (
                <FormControlLabel
                  key={d.key}
                  control={
                    <Checkbox
                      checked={form.dispatch_types.includes(d.key)}
                      onChange={() => handleDispatchToggle(d.key)}
                    />
                  }
                  label={d.label}
                />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        {/* 출발지 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>상차지 정보</Typography>
              <FormControlLabel
                control={<Checkbox checked={sameAsOrigin} onChange={(e) => handleSameAsOriginToggle(e.target.checked)} />}
                label="내 정보에서 가져오기"
                sx={{ m: 0 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth required label="상차지 주소 *"
                  value={form.origin_address}
                  onChange={(e) => setForm({ ...form, origin_address: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" variant="outlined" startIcon={<Search />}
                          onClick={() => openAddressSearch((addr) => setForm((p) => ({ ...p, origin_address: addr })))}>검색</Button>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="상차지 담당자 이름" value={form.origin_contact_name}
                  onChange={(e) => setForm({ ...form, origin_contact_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="상차지 연락처" value={form.origin_contact}
                  onChange={(e) => setForm({ ...form, origin_contact: formatPhone(e.target.value) })}
                  placeholder="010-1234-5678" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 경유지 */}
        {isWaypoint && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6" fontWeight={600}>경유지</Typography>
                <Button size="small" startIcon={<AddCircleOutline />}
                  onClick={() => setForm({ ...form, waypoints: [...form.waypoints, { address: '' }] })}>
                  경유지 추가
                </Button>
              </Box>
              {form.waypoints.map((wp, i) => (
                <Box key={i} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary">경유지 {i + 1}</Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={!!sameAsWp[i]} onChange={(e) => handleSameAsWpToggle(i, e.target.checked)} />}
                        label={<Typography variant="caption">내 정보에서 가져오기</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Box>
                    <IconButton size="small" color="error"
                      onClick={() => {
                        setForm({ ...form, waypoints: form.waypoints.filter((_, idx) => idx !== i) });
                        setSameAsWp((s) => { const n = { ...s }; delete n[i]; return n; });
                      }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField fullWidth required label="주소 *" value={wp.address || ''}
                        onChange={(e) => {
                          const next = [...form.waypoints];
                          next[i] = { ...next[i], address: e.target.value };
                          setForm({ ...form, waypoints: next });
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button size="small" variant="outlined" startIcon={<Search />}
                                onClick={() => openAddressSearch((addr) => {
                                  const next = [...form.waypoints];
                                  next[i] = { ...next[i], address: addr };
                                  setForm({ ...form, waypoints: next });
                                })}>검색</Button>
                            </InputAdornment>
                          ),
                        }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="담당자" value={wp.contact_name || ''}
                        onChange={(e) => {
                          const next = [...form.waypoints];
                          next[i] = { ...next[i], contact_name: e.target.value };
                          setForm({ ...form, waypoints: next });
                        }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="연락처" value={wp.contact || ''}
                        onChange={(e) => {
                          const next = [...form.waypoints];
                          next[i] = { ...next[i], contact: formatPhone(e.target.value) };
                          setForm({ ...form, waypoints: next });
                        }}
                        placeholder="010-1234-5678" />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* 도착지 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>하차지 정보</Typography>
              <FormControlLabel
                control={<Checkbox checked={sameAsDest} onChange={(e) => handleSameAsDestToggle(e.target.checked)} />}
                label="내 정보에서 가져오기"
                sx={{ m: 0 }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth required label="하차지 주소 *"
                  value={form.destination_address}
                  onChange={(e) => setForm({ ...form, destination_address: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" variant="outlined" startIcon={<Search />}
                          onClick={() => openAddressSearch((addr) => setForm((p) => ({ ...p, destination_address: addr })))}>검색</Button>
                      </InputAdornment>
                    ),
                  }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="하차지 담당자 이름" value={form.destination_contact_name}
                  onChange={(e) => setForm({ ...form, destination_contact_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="하차지 연락처" value={form.destination_contact}
                  onChange={(e) => setForm({ ...form, destination_contact: formatPhone(e.target.value) })}
                  placeholder="010-9876-5432" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 상차일시 */}
        <DateTimePresetField
          title="상차일시"
          presets={[
            { key: 'now',      label: '지금' },
            { key: 'today',    label: '당일 상차' },
            { key: 'tomorrow', label: '내일 상차' },
            { key: 'monday',   label: '월요일 상차' },
          ]}
          value={form.pickup}
          onChange={(v) => setForm({ ...form, pickup: v })}
          bgcolor="#f1f8e9"
        />

        {/* 상차 방법 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>상차 방법</Typography>
            <FormGroup row>
              {HANDLING_METHODS.map((m) => (
                <FormControlLabel
                  key={m}
                  control={<Checkbox checked={form.loading_methods.includes(m)} onChange={() => toggleListItem('loading_methods', m)} />}
                  label={m}
                />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        {/* 하차일시 */}
        <DateTimePresetField
          title="하차일시"
          presets={[
            { key: 'today',    label: '당일 하차' },
            { key: 'tomorrow', label: '내일 하차' },
            { key: 'monday',   label: '월요일 하차' },
          ]}
          value={form.delivery}
          onChange={(v) => setForm({ ...form, delivery: v })}
          bgcolor="#fff8e1"
        />

        {/* 하차 방법 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>하차 방법</Typography>
            <FormGroup row>
              {HANDLING_METHODS.map((m) => (
                <FormControlLabel
                  key={m}
                  control={<Checkbox checked={form.unloading_methods.includes(m)} onChange={() => toggleListItem('unloading_methods', m)} />}
                  label={m}
                />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        {/* 차종 + 톤수 (한 줄) */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  차종 <Typography component="span" variant="caption" color="text.secondary">(최대 3개)</Typography>
                </Typography>
                <Autocomplete
                  multiple options={VEHICLE_TYPES}
                  value={form.vehicle_types}
                  onChange={(_, v) => { if (v.length <= 3) setForm({ ...form, vehicle_types: v }); }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="filled" color="primary" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} placeholder="차종 선택" />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  톤수 <Typography component="span" variant="caption" color="text.secondary">(최대 3개)</Typography>
                </Typography>
                <Autocomplete
                  multiple options={WEIGHT_OPTIONS}
                  value={form.vehicle_weights}
                  onChange={(_, v) => { if (v.length <= 3) setForm({ ...form, vehicle_weights: v }); }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="filled" color="secondary" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} placeholder="톤수 선택" />}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 화물 사진 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>화물 사진</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>최대 5장, 각 10MB 이하</Typography>
            <input type="file" accept="image/*" multiple ref={fileInputRef}
              onChange={handlePhotoSelect} style={{ display: 'none' }} />
            <Button variant="outlined" startIcon={<PhotoCamera />}
              onClick={() => fileInputRef.current.click()} disabled={photos.length >= 5} sx={{ mb: 2 }}>
              사진 선택 ({photos.length}/5)
            </Button>
            {photoPreviews.length > 0 && (
              <Grid container spacing={1}>
                {photoPreviews.map((src, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Box sx={{ position: 'relative' }}>
                      <img src={src} alt={`화물 ${i + 1}`}
                        style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                      <Chip icon={<Delete />} label="삭제" size="small" color="error"
                        onClick={() => removePhoto(i)}
                        sx={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer' }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* 특이사항 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>특이사항</Typography>
            <TextField fullWidth multiline rows={3} value={form.special_notes}
              onChange={(e) => setForm({ ...form, special_notes: e.target.value })}
              placeholder="화물 종류, 주의사항, 요청사항 등을 입력하세요" />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/orders')} disabled={loading}>취소</Button>
          <Button type="submit" variant="contained" size="large"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Save />}
            disabled={loading}>
            {loading ? '접수 중...' : '배차 신청'}
          </Button>
        </Box>
      </form>
    </CustomerLayout>
  );
}
